import {
  type Location,
  type Member,
  type NewLocation,
  prepareNewLocation,
  prepareRename,
  type UserProfile,
} from "@larder/shared";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  type Unsubscribe,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { functions } from "./extract";
import { seedDefaultStorages } from "./storages";

/**
 * Repository layer for locations and the user profile.
 *
 * Every Firestore call for these entities goes through this module — no `db`
 * import in a component. "We'll swap the backend later" only stays true if you
 * can point at the seam. See docs/engineering/data-model.md.
 */

type Stored<T> = Omit<T, "id">;

/**
 * Creates the location, its owner membership, and the user profile pointer.
 *
 * NOT a batch, and that ordering is load-bearing. The rule guarding the member
 * document calls `get()` on the location, and a `get()` inside a batched write
 * sees the state BEFORE the batch — so a single atomic write is denied. The
 * emulator proves this in tests-rules/firestore.test.ts.
 *
 * The cost of writing sequentially is a crash between calls leaving a location
 * with no members. That document is then unreadable by anyone (the read rule
 * needs membership), so it is inert rather than dangerous, and the user simply
 * creates the location again.
 */
export async function createLocation(
  uid: string,
  input: NewLocation,
  /** Names this account already has — a duplicate is refused before any write. */
  taken: readonly string[] = [],
): Promise<Location> {
  const { name, type, timezone } = prepareNewLocation(input, taken);
  const ref = doc(collection(db, "locations"));

  const stored: Stored<Location> = {
    name,
    type,
    ownerUid: uid,
    timezone,
    createdAt: Date.now(),
  };
  await setDoc(ref, stored);

  const member: Member = {
    uid,
    locationId: ref.id,
    role: "owner",
    joinedAt: Date.now(),
  };
  await setDoc(doc(db, "locations", ref.id, "members", uid), member);

  await setDefaultLocation(uid, ref.id);

  // Somewhere to put things, so the first item never asks the user to invent a
  // storage place before they have added anything.
  await seedDefaultStorages(ref.id);

  return { id: ref.id, ...stored };
}

/**
 * Creates the profile document if it is missing, without touching `createdAt`
 * on an existing one. Called once per session rather than merged into every
 * write, so "when did this household start" stays a real number.
 */
export async function ensureProfile(uid: string): Promise<UserProfile> {
  const existing = await readProfile(uid);
  if (existing) return existing;
  const profile: UserProfile = {
    uid,
    createdAt: Date.now(),
    defaultLocationId: null,
  };
  await setDoc(doc(db, "users", uid), profile);
  return profile;
}

/**
 * Live list of the locations this user belongs to.
 *
 * The membership query is the source of truth rather than a `locationIds` array
 * on the user, because an invite is written by the INVITER and cannot touch the
 * invitee's own document.
 *
 * PENDING WRITES ARE SKIPPED, and that is not an optimisation. Firestore's
 * latency compensation delivers a snapshot the instant a local write is issued,
 * before the server has it. Acting on that snapshot means fetching the location
 * from the SERVER, where the membership does not exist yet, and the rules
 * correctly deny it — which surfaced as "can't reach your data" immediately
 * after creating a household.
 *
 * `includeMetadataChanges` is required for the fix to work: without it, the
 * pending -> acknowledged transition is a metadata-only change and Firestore
 * would never re-deliver the snapshot we skipped.
 */
export function watchMyLocations(
  uid: string,
  onChange: (locations: Location[]) => void,
  onError: (e: Error) => void,
): Unsubscribe {
  const memberships = query(collectionGroup(db, "members"), where("uid", "==", uid));

  // One live listener per household, keyed by id, so a rename lands in the
  // switcher and in settings the moment it is written. Membership changes
  // only add or drop listeners; they do not refetch the ones already open.
  const open = new Map<string, { stop: Unsubscribe; doc: Location | null }>();

  const publish = () => {
    const locations = [...open.values()]
      .map((l) => l.doc)
      .filter((d): d is Location => d !== null)
      // Stable order so the switcher doesn't reshuffle between snapshots.
      .sort((a, b) => a.createdAt - b.createdAt);
    onChange(locations);
  };

  const stopMembers = onSnapshot(
    memberships,
    { includeMetadataChanges: true },
    (snap) => {
      if (snap.metadata.hasPendingWrites) return;
      const ids = new Set(snap.docs.map((d) => (d.data() as Member).locationId));

      for (const [id, l] of open) {
        if (!ids.has(id)) {
          l.stop();
          open.delete(id);
        }
      }
      for (const id of ids) {
        if (open.has(id)) continue;
        const entry = { stop: () => {}, doc: null as Location | null };
        open.set(id, entry);
        entry.stop = onSnapshot(
          doc(db, "locations", id),
          (d) => {
            // Gone (or never readable): drop it rather than show a ghost. The
            // delete cascade removes our membership a beat later anyway.
            entry.doc = d.exists()
              ? { id: d.id, ...(d.data() as Stored<Location>) }
              : null;
            publish();
          },
          (e) => {
            // A location we cannot read is a membership the server has not
            // caught up with yet, or one that was just deleted under us.
            // Neither is fatal to the list; the entry simply stays empty.
            if ((e as { code?: string }).code === "permission-denied") {
              entry.doc = null;
              publish();
              return;
            }
            onError(e);
          },
        );
      }
      // No households at all: say so, so the hook can seed one.
      if (ids.size === 0) publish();
    },
    onError,
  );

  return () => {
    stopMembers();
    for (const l of open.values()) l.stop();
    open.clear();
  };
}

/**
 * Renames a household. The type follows the name (see `prepareRename`), so
 * renaming "Office" to "Flat" stops it being typed as an office.
 *
 * Owner-only at the rules, so this rejects for a member — the UI does not
 * offer it to them, and the error is shown if it happens anyway.
 */
export async function renameLocation(
  locationId: string,
  rawName: string,
  /** The OTHER places' names; the one being renamed is not its own clash. */
  taken: readonly string[] = [],
): Promise<void> {
  const { name, type } = prepareRename(rawName, taken);
  await updateDoc(doc(db, "locations", locationId), { name, type });
}

const askDelete = httpsCallable<{ locationId: string }, { ok: true }>(
  functions,
  "deleteLocation",
  { timeout: 120_000 },
);

/**
 * Removes a household and everything in it, through a Cloud Function — the
 * client cannot cascade the delete itself because `events` is append-only to
 * it. The profile pointer is cleared if it named this place, so the next
 * session opens into a real one rather than a missing id.
 */
export async function deleteLocation(uid: string, locationId: string): Promise<void> {
  await askDelete({ locationId });
  const profile = await readProfile(uid);
  if (profile?.defaultLocationId === locationId) {
    await setDoc(doc(db, "users", uid), { defaultLocationId: null }, { merge: true });
  }
}

export async function setDefaultLocation(uid: string, locationId: string): Promise<void> {
  await setDoc(doc(db, "users", uid), { defaultLocationId: locationId }, { merge: true });
}

/**
 * Remembers which city this household's shop prices are quoted for.
 *
 * Merged onto the household document, so it survives a rename and is shared by
 * everyone in it — a flatmate should not be asked for their location again to
 * see the price of the milk they both buy.
 *
 * Only the OWNER may write here (see firestore.rules), so this can reject for a
 * member. That is a soft failure by design: the caller carries the resolved
 * city for the session anyway, and prices still show. It just gets asked again
 * next time.
 */
export async function setShopLocation(
  locationId: string,
  where: NonNullable<Location["shopLocation"]>,
): Promise<void> {
  await setDoc(
    doc(db, "locations", locationId),
    { shopLocation: where },
    { merge: true },
  );
}

export async function readProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? (snap.data() as UserProfile) : null;
}
